import React, { useState, useMemo, useRef } from 'react';
import { generateId, saveCustomColorScheme, DEFAULT_COLOR_SCHEME } from '@/theme/customThemes';
import { useTheme } from '@/context/ThemeContext';
import { createPersonalTheme, updatePersonalTheme, uploadThemeVideo } from '@/api/themeApi';

/* ════════════════════════════════════════════════════════════
   Colour-scheme editor — create or edit a custom colour scheme
   ════════════════════════════════════════════════════════════ */

/** Colour groups for the editor form */
const GROUPS = [
  {
    label: 'Brand',
    fields: [
      { key: 'primary',        label: 'Primary' },
      { key: 'primaryHover',   label: 'Primary Hover' },
      { key: 'secondary',      label: 'Secondary' },
      { key: 'secondaryHover', label: 'Secondary Hover' },
      { key: 'accent',         label: 'Accent' },
    ],
  },
  {
    label: 'Backgrounds',
    fields: [
      { key: 'backgroundBody',    label: 'Body' },
      { key: 'backgroundSurface', label: 'Surface / Card' },
      { key: 'backgroundInput',   label: 'Input' },
    ],
  },
  {
    label: 'Text',
    fields: [
      { key: 'textPrimary',   label: 'Primary' },
      { key: 'textSecondary', label: 'Secondary' },
      { key: 'textAccent',    label: 'On-Primary' },
    ],
  },
  {
    label: 'Border',
    fields: [
      { key: 'borderDefault', label: 'Default' },
    ],
  },
  {
    label: 'Status',
    fields: [
      { key: 'destructive',     label: 'Destructive' },
      { key: 'destructiveHover', label: 'Destructive Hover' },
      { key: 'success',          label: 'Success' },
      { key: 'successHover',     label: 'Success Hover' },
      { key: 'warning',          label: 'Warning' },
      { key: 'info',             label: 'Info' },
    ],
  },
  {
    label: 'Sidebar',
    fields: [
      { key: 'sidebar',     label: 'Background' },
      { key: 'sidebarText', label: 'Text' },
    ],
  },
];

const EMOJI_OPTIONS = ['🎨', '🌈', '🔵', '🟢', '🟠', '🟣', '❤️', '⚡', '💎', '🌊', '🌿', '🔥'];

export default function ColourSchemeEditor({ existing, onClose }) {
  const { refreshCustomThemes, setColorScheme, setBackgroundVideo } = useTheme();
  const isEditing = !!existing?.id;

  const [label, setLabel] = useState(existing?.label || '');
  const [icon, setIcon]   = useState(existing?.icon || '🎨');
  const [isDark, setIsDark] = useState(existing?.isDark ?? false);
  const [videoUrl, setVideoUrl] = useState(existing?.colors?.videoUrl || '');
  const [videoOpacity, setVideoOpacity] = useState(existing?.colors?.videoOverlayOpacity ?? 0.7);
  const videoInputRef = useRef(null);
  const [videoUploading, setVideoUploading] = useState(false);
  const [videoUploadPct, setVideoUploadPct] = useState(0);
  const [videoUploadErr, setVideoUploadErr] = useState('');
  const [colors, setColors] = useState(() => {
    if (existing?.colors) return { ...DEFAULT_COLOR_SCHEME, ...existing.colors };
    return { ...DEFAULT_COLOR_SCHEME };
  });

  const id = useMemo(
    () => existing?.id || (label ? generateId(label) : ''),
    [existing, label],
  );

  const updateColor = (key, value) => setColors((prev) => ({ ...prev, [key]: value }));

  const handleSave = () => {
    if (!label.trim()) return;
    const finalId = existing?.id || generateId(label);
    // Merge video fields into colors for localStorage
    const colorsWithVideo = { ...colors };
    if (videoUrl.trim()) {
      colorsWithVideo.videoUrl = videoUrl.trim();
      colorsWithVideo.videoOverlayOpacity = videoOpacity;
    } else {
      delete colorsWithVideo.videoUrl;
      delete colorsWithVideo.videoOverlayOpacity;
    }
    // Save to localStorage (instant)
    saveCustomColorScheme({ id: finalId, label: label.trim(), icon, isDark, colors: colorsWithVideo });
    // Persist to backend (best-effort)
    const payload = {
      theme_id: finalId,
      theme_type: 'color',
      label: label.trim(),
      icon,
      is_dark: isDark,
      data: colorsWithVideo,
    };
    const token = localStorage.getItem('token');
    if (token) {
      (isEditing ? updatePersonalTheme(finalId, payload) : createPersonalTheme(payload))
        .catch(() => { /* localStorage is the fallback */ });
    }
    // Also apply the video background if set
    if (videoUrl.trim()) {
      setBackgroundVideo({ url: videoUrl.trim(), overlayOpacity: videoOpacity });
    }
    refreshCustomThemes();
    setColorScheme(finalId);
    onClose();
  };

  /* ── Mini live preview ── */
  const Preview = () => (
    <div
      className="rounded-lg overflow-hidden border w-full h-28"
      style={{ background: colors.backgroundBody, borderColor: colors.borderDefault }}
    >
      {/* top bar */}
      <div className="h-6 flex items-center px-2 gap-1" style={{ background: colors.primary }}>
        <span className="text-[10px] font-semibold" style={{ color: colors.textAccent }}>Title Bar</span>
      </div>
      {/* body */}
      <div className="flex h-[calc(100%-1.5rem)]">
        {/* sidebar */}
        <div className="w-10 shrink-0 p-1 flex flex-col gap-1" style={{ background: colors.sidebar }}>
          <div className="w-full h-2 rounded" style={{ background: colors.sidebarText, opacity: 0.5 }} />
          <div className="w-full h-2 rounded" style={{ background: colors.sidebarText, opacity: 0.3 }} />
        </div>
        {/* content */}
        <div className="flex-1 p-2 flex flex-col gap-1" style={{ background: colors.backgroundSurface }}>
          <div className="text-[9px]" style={{ color: colors.textPrimary }}>Heading</div>
          <div className="text-[8px]" style={{ color: colors.textSecondary }}>Secondary text here</div>
          <div className="flex gap-1 mt-auto">
            <div className="h-4 px-2 rounded text-[8px] flex items-center" style={{ background: colors.primary, color: colors.textAccent }}>Save</div>
            <div className="h-4 px-2 rounded text-[8px] flex items-center" style={{ background: colors.destructive, color: '#fff' }}>Delete</div>
            <div className="h-4 px-2 rounded text-[8px] flex items-center" style={{ background: colors.success, color: '#fff' }}>OK</div>
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <div className="bg-backgroundSurface rounded-lg border border-borderDefault p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold text-textPrimary">
          {isEditing ? 'Edit Colour Scheme' : 'Create Colour Scheme'}
        </h3>
        <button onClick={onClose} className="text-textSecondary hover:text-textPrimary text-xl leading-none">&times;</button>
      </div>

      {/* Meta */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div>
          <label className="block text-xs font-medium text-textSecondary mb-1">Name</label>
          <input
            type="text"
            value={label}
            onChange={(e) => setLabel(e.target.value)}
            placeholder="My Custom Theme"
            className="w-full px-3 py-2 rounded-md bg-backgroundInput border border-borderDefault text-textPrimary text-sm focus:outline-none focus:ring-2 focus:ring-primary"
          />
        </div>
        <div>
          <label className="block text-xs font-medium text-textSecondary mb-1">Icon</label>
          <div className="flex flex-wrap gap-1">
            {EMOJI_OPTIONS.map((e) => (
              <button
                key={e}
                onClick={() => setIcon(e)}
                className={`w-8 h-8 rounded flex items-center justify-center text-base border transition-colors ${
                  icon === e ? 'border-primary bg-primary/10' : 'border-transparent hover:bg-backgroundInput'
                }`}
              >
                {e}
              </button>
            ))}
          </div>
        </div>
        <div>
          <label className="block text-xs font-medium text-textSecondary mb-1">Mode</label>
          <div className="flex gap-2 mt-1">
            <button
              onClick={() => setIsDark(false)}
              className={`px-3 py-1.5 rounded text-sm border transition-colors ${
                !isDark ? 'border-primary bg-primary/10 text-primary' : 'border-borderDefault text-textSecondary hover:bg-backgroundInput'
              }`}
            >
              ☀️ Light
            </button>
            <button
              onClick={() => setIsDark(true)}
              className={`px-3 py-1.5 rounded text-sm border transition-colors ${
                isDark ? 'border-primary bg-primary/10 text-primary' : 'border-borderDefault text-textSecondary hover:bg-backgroundInput'
              }`}
            >
              🌙 Dark
            </button>
          </div>
        </div>
      </div>

      {/* Live preview */}
      <div>
        <label className="block text-xs font-medium text-textSecondary mb-2">Live Preview</label>
        <Preview />
      </div>

      {/* Video background */}
      <div>
        <h4 className="text-xs font-semibold text-textSecondary uppercase tracking-wider mb-2">Background Video (Optional)</h4>
        <div className="space-y-3">
          <div className="flex gap-2 flex-wrap">
            <button
              type="button"
              onClick={() => videoInputRef.current?.click()}
              disabled={videoUploading}
              className="inline-flex items-center gap-2 px-3 py-2 rounded-md text-sm bg-primary text-textAccent hover:bg-primaryHover transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {videoUploading ? `Uploading ${videoUploadPct}%` : 'Upload'}
            </button>
            <input
              ref={videoInputRef}
              type="file"
              accept="video/mp4,video/webm,video/ogg"
              className="hidden"
              onChange={async (e) => {
                const file = e.target.files?.[0];
                if (!file) return;
                e.target.value = '';
                if (file.size > 50 * 1024 * 1024) { setVideoUploadErr('Max 50 MB.'); return; }
                setVideoUploadErr('');
                setVideoUploading(true);
                setVideoUploadPct(0);
                try {
                  const res = await uploadThemeVideo(file, (p) => setVideoUploadPct(p));
                  setVideoUrl(res.url);
                } catch (err) {
                  setVideoUploadErr(err?.response?.data?.error || 'Upload failed.');
                } finally { setVideoUploading(false); }
              }}
            />
            <input
              type="text"
              value={videoUrl}
              onChange={(e) => setVideoUrl(e.target.value)}
              placeholder="…or paste a URL"
              className="flex-1 min-w-[180px] px-3 py-2 rounded-md bg-backgroundInput border border-borderDefault text-textPrimary text-sm focus:outline-none focus:ring-2 focus:ring-primary font-mono"
            />
            {videoUrl && (
              <button onClick={() => setVideoUrl('')} className="px-2 py-2 rounded-md text-sm border border-borderDefault text-destructive hover:bg-destructive/10" title="Clear">&times;</button>
            )}
          </div>
          {videoUploading && (
            <div className="h-1.5 bg-backgroundInput rounded-full overflow-hidden max-w-xs">
              <div className="h-full bg-primary transition-all duration-200" style={{ width: `${videoUploadPct}%` }} />
            </div>
          )}
          {videoUploadErr && <p className="text-xs text-destructive">{videoUploadErr}</p>}
          {videoUrl.trim() && (
            <div>
              <label className="block text-[11px] text-textSecondary mb-1">Overlay Opacity: {Math.round(videoOpacity * 100)}%</label>
              <input
                type="range"
                min="0"
                max="100"
                value={Math.round(videoOpacity * 100)}
                onChange={(e) => setVideoOpacity(Number(e.target.value) / 100)}
                className="w-full max-w-xs accent-primary"
              />
            </div>
          )}
        </div>
      </div>

      {/* Colour groups */}
      <div className="space-y-4">
        {GROUPS.map((group) => (
          <div key={group.label}>
            <h4 className="text-xs font-semibold text-textSecondary uppercase tracking-wider mb-2">{group.label}</h4>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-3">
              {group.fields.map(({ key, label: fieldLabel }) => (
                <div key={key} className="flex items-center gap-2">
                  <div className="relative">
                    <input
                      type="color"
                      value={colors[key]}
                      onChange={(e) => updateColor(key, e.target.value)}
                      className="w-8 h-8 rounded cursor-pointer border border-borderDefault bg-transparent appearance-none"
                      style={{ padding: 0 }}
                    />
                  </div>
                  <div className="flex-1 min-w-0">
                    <span className="text-[11px] text-textSecondary block truncate">{fieldLabel}</span>
                    <input
                      type="text"
                      value={colors[key]}
                      onChange={(e) => updateColor(key, e.target.value)}
                      className="w-full text-[10px] text-textPrimary bg-transparent border-b border-borderDefault focus:outline-none focus:border-primary font-mono"
                    />
                  </div>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>

      {/* Actions */}
      <div className="flex justify-end gap-3 pt-2 border-t border-borderDefault">
        <button
          onClick={onClose}
          className="px-4 py-2 rounded-md text-sm border border-borderDefault text-textSecondary hover:bg-backgroundInput transition-colors"
        >
          Cancel
        </button>
        <button
          onClick={handleSave}
          disabled={!label.trim()}
          className="px-4 py-2 rounded-md text-sm bg-primary text-textAccent hover:bg-primaryHover transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {isEditing ? 'Save Changes' : 'Create Colour Scheme'}
        </button>
      </div>
    </div>
  );
}
